import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import adminApi, { errMsg } from '../../api';
import { useAdminAuth, hasRole } from '../../auth';
import { Band, Card, Field } from '../../components/Card';
import DataTable from '../../components/DataTable';
import EmptyState from '../../components/EmptyState';
import PageHeader from '../../components/PageHeader';
import StatusPill from '../../components/StatusPill';
import { formatDate, formatDateTime, formatDuration, formatRelative, humanize } from '../../format';

/**
 * One person.
 *
 * Reaching this page is a deliberate, logged action — the backend writes a
 * read-type audit row on open, naming who looked. The banner says so, and
 * says it plainly rather than in small grey text at the bottom, because the
 * person reading it should know while they are reading, not after.
 *
 * New here: transcripts and their saved items are now reachable as links
 * rather than a "deferred to a later phase" placeholder box, since both
 * exist. The transcript link only renders for an owner, which is who the
 * backend allows.
 */
const ACTION_LABEL = {
  'product.impression': ['neutral', 'Viewed'],
  'product.saved': ['good', 'Saved'],
  'product.passed': ['neutral', 'Passed'],
  'buy.clicked': ['info', 'Buy click'],
};

export default function UserDetailPage() {
  const { userId } = useParams();
  const { admin } = useAdminAuth();
  const canReadTranscripts = hasRole(admin, 'owner');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await adminApi.get(`/users/${encodeURIComponent(userId)}`);
        setData(data);
      } catch (err) {
        setError(errMsg(err, 'Could not load this user'));
      } finally {
        setLoading(false);
      }
    })();
  }, [userId]);

  if (loading) return <Card><p className="text-sm text-ink-muted">Loading…</p></Card>;
  if (error) {
    return (
      <div>
        <PageHeader title="User" back={{ to: '/growth/people', label: 'Find a user' }} />
        <EmptyState title="Could not load this user" detail={error} />
      </div>
    );
  }
  if (!data) return null;

  const { user, profile, actions_timeline: actions, sessions, login_source: loginSource, tryons } = data;
  const age = profile?.birth_year ? new Date().getFullYear() - profile.birth_year : null;
  const lookback = data.lookback_days;

  const sessionColumns = [
    { key: 'start_ts', label: 'Started', render: (s) => formatDateTime(s.start_ts) },
    { key: 'device', label: 'Device', render: (s) => `${s.device_platform || '?'} / ${s.device_client || '?'}` },
    { key: 'duration_s', label: 'Duration', align: 'right', render: (s) => formatDuration(s.duration_s) },
  ];

  const actionColumns = [
    { key: 'event_ts', label: 'When', render: (a) => <span title={formatDateTime(a.event_ts)}>{formatRelative(a.event_ts)}</span> },
    {
      key: 'event_name',
      label: 'What',
      render: (a) => {
        const [tone, label] = ACTION_LABEL[a.event_name] || ['neutral', a.event_name];
        return <StatusPill tone={tone} label={label} />;
      },
    },
    { key: 'surface', label: 'Where', render: (a) => humanize(a.surface) },
    {
      key: 'product_id',
      label: 'Product',
      render: (a) => (a.product_id
        ? (
          <Link
            to={`/product/${encodeURIComponent(a.product_id)}`}
            className="font-mono text-[11px] text-raspberry-600 hover:text-raspberry-700"
          >
            {a.product_id}
          </Link>
        )
        : null),
    },
  ];

  const tryonColumns = [
    {
      key: 'product',
      label: 'Product',
      render: (t) => (t.mira_sku
        ? (
          <Link to={`/product/${encodeURIComponent(t.mira_sku)}`} className="text-ink hover:text-raspberry-600">
            {t.product_title || t.mira_sku}
          </Link>
        )
        : t.product_title),
    },
    { key: 'product_brand', label: 'Brand' },
    { key: 'created_at', label: 'When', render: (t) => formatDate(t.created_at) },
    { key: 'status', label: 'Status', render: (t) => <StatusPill status={t.status === 'complete' ? 'succeeded' : t.status} label={humanize(t.status)} /> },
  ];

  const listOrMessage = (value) => (Array.isArray(value) ? value : []);
  const errorDetail = (value) => (value && !Array.isArray(value) && value.status === 'error' ? value.detail : null);

  return (
    <div>
      <PageHeader
        title={user.name || '(no name)'}
        back={{ to: '/growth/people', label: 'Find a user' }}
        hint={user.phone}
        actions={canReadTranscripts && (
          <Link
            to="/ai/transcripts"
            className="rounded-lg border border-hairline px-2.5 py-1.5 text-xs font-medium text-ink-secondary hover:bg-surface-page hover:text-ink"
          >
            Transcripts
          </Link>
        )}
      />

      <p className="mb-6 rounded-lg border border-status-warning/40 bg-status-warning/5 px-3.5 py-2.5 text-xs leading-relaxed text-ink-secondary">
        <span className="font-semibold text-ink">Opening this page was logged.</span> A read of someone&apos;s
        activity is audited exactly like a change to it, with your name on the row. It is visible to anyone on
        the Audit tab.
      </p>

      <Band title="Profile">
        <Card>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3 lg:grid-cols-6">
            <Field label="Phone">{user.phone}</Field>
            <Field label="Gender">{humanize(user.gender)}</Field>
            <Field label="Age">{age}</Field>
            <Field label="State">{profile?.state}</Field>
            <Field label="Signed up">{formatDate(user.created_at)}</Field>
            <Field label="User id" mono>{user.user_id}</Field>
          </dl>
        </Card>
      </Band>

      <Band title={`Signs in from — last ${lookback} days`}>
        {!loginSource || Object.keys(loginSource).length === 0 ? (
          <EmptyState title="No login activity in this window" />
        ) : (
          <div className="flex flex-wrap gap-2">
            {Object.entries(loginSource).map(([key, count]) => (
              <span key={key} className="rounded-full border border-hairline bg-surface px-3 py-1.5 text-xs text-ink-secondary">
                {humanize(key)} <span className="ml-1 font-semibold text-ink">{count}</span>
              </span>
            ))}
          </div>
        )}
      </Band>

      <Band title={`Sessions — last ${lookback} days`}>
        <DataTable
          columns={sessionColumns}
          rows={listOrMessage(sessions)}
          rowKey={(s) => s.session_id}
          dense
          maxHeight="20rem"
          empty={
            <EmptyState
              title={errorDetail(sessions) ? 'Could not load sessions' : 'No sessions in this window'}
              detail={errorDetail(sessions) || undefined}
            />
          }
        />
      </Band>

      <Band title={`Activity — last ${lookback} days`}>
        <DataTable
          columns={actionColumns}
          rows={listOrMessage(actions)}
          rowKey={(a, i) => `${a.event_ts}-${a.event_name}-${a.product_id || i}`}
          dense
          maxHeight="26rem"
          empty={
            <EmptyState
              title={errorDetail(actions) ? 'Could not load activity' : 'No recorded activity in this window'}
              detail={errorDetail(actions) || undefined}
            />
          }
        />
      </Band>

      <Band title="Try-ons">
        <DataTable
          columns={tryonColumns}
          rows={tryons || []}
          rowKey={(t) => t.tryon_id}
          dense
          maxHeight="20rem"
          empty={<EmptyState title="No try-ons yet" />}
        />
      </Band>
    </div>
  );
}
